import { useId, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SUPERSEDED_VALUE_CLASS } from '../../forecast/details/inline-overrides/overrideMarking';
import { isQuantityEditable, quantityDiff } from '../utils/lineQuantity';
import { CalculatedDescriptionContext } from './calculatedDescription';

type QuantityLine = Parameters<typeof quantityDiff>[0];

/**
 * The engine's quantity, beside the buyer's Qty. Once the buyer overrides it,
 * it stays in view struck through, as the Calculated column does on Forecast
 * Details.
 */
export function CalcQuantityCell({ line }: { line: QuantityLine }) {
  const gt = useGT();
  const diff = quantityDiff(line);
  const calculated = diff.calculated.toLocaleString();
  if (!diff.differs) {
    return (
      <span className="text-xs tabular-nums text-muted-foreground">
        {calculated}
      </span>
    );
  }
  const label = gt('Calculated {calculated}, overridden to {adjusted}', {
    context: 'cart line calc quantity, the buyer overrode the quantity',
    calculated,
    adjusted: diff.adjusted.toLocaleString(),
  });
  return (
    <span
      title={label}
      aria-label={label}
      className={cn('text-xs tabular-nums', SUPERSEDED_VALUE_CLASS)}
    >
      {calculated}
    </span>
  );
}

/**
 * An overridden quantity names the engine's on hover (and on focus), and to
 * screen readers, which do not reach the tooltip. An editable quantity is
 * focused through its input; a read-only one has none, so the trigger itself
 * takes the tab stop.
 */
export function CalculatedOnHover({
  line,
  children,
}: {
  line: QuantityLine & Parameters<typeof isQuantityEditable>[0];
  children: ReactNode;
}) {
  const gt = useGT();
  const descriptionId = useId();
  const diff = quantityDiff(line);
  if (!diff.differs) return children;
  const label = gt('Calculated {calculated}', {
    context:
      'cart line quantity tooltip, the engine quantity the buyer overrode',
    calculated: diff.calculated.toLocaleString(),
  });
  const readOnly = !isQuantityEditable(line);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={readOnly ? 0 : undefined}
            className={cn(
              'block',
              readOnly &&
                'rounded outline-offset-2 focus-visible:outline-2 focus-visible:outline-ring',
            )}
          >
            <CalculatedDescriptionContext value={descriptionId}>
              {children}
            </CalculatedDescriptionContext>
            <span id={descriptionId} className="sr-only">
              {label}
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
